import '../App.css'
import {
	Container, Title, Card, Text, Space, Pagination, Group, Center,
	Badge, Divider, createStyles, rem, Stack, Affix, Transition, Button,
	Loader
} from '@mantine/core'
import Footer from '../components/Footer';
import Head from '../components/Head';
import ReactMarkdown from 'react-markdown';
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { IconArrowUp, IconPencil } from '@tabler/icons-react'

// ── Blog data ────────────────────────────────────────────────────────────────
const blogPosts = [
	{
		id: 4,
		title: "Eventbrite gets acquired...what's next?",
		date: "Jan 5, 2026",
		summary: "Eventbrite is being acquired by Bending Spoons. Here is what that means for producers and why you might want to look at alternatives like Comedy Seen.",
		imageUrl: "https://www.getcomedyseen.com/social-share-card.png",

	},
	{
		id: 3,
		title: "Two thousand and...Forever?!",
		date: "Dec 26, 2025",
		summary: "Looking forward to 2026!",
		imageUrl: "https://www.getcomedyseen.com/social-share-card.png",

	},
	{
		id: 1,
		title: "2027",
		date: "Nov 14, 2023",
		summary: "2023 end of year thoughts",
		imageUrl: "https://www.getcomedyseen.com/social-share-card.png",

	},
	{
		id: 2,
		title: "Comedy Cash",
		date: "Sep 19, 2023",
		summary: "Name change and other things",
		imageUrl: "https://www.getcomedyseen.com/social-share-card.png",

	}
];

// ── Styles ───────────────────────────────────────────────────────────────────
const useStyles = createStyles((theme) => ({

	heroWrapper: {
		background: 'radial-gradient(ellipse at 50% 60%, #f0e6ff 0%, #e8f6fb 50%, #f4fbff 100%)',
		paddingTop: rem(32),
		paddingBottom: rem(36),
		textAlign: 'center',
	},

	blogTitle: {
		fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
		fontWeight: 800,
		letterSpacing: '-0.02em',
		fontSize: rem(60),
		color: '#5c0099',

		[theme.fn.smallerThan('md')]: { fontSize: rem(44) },
		[theme.fn.smallerThan('sm')]: { fontSize: rem(34) },
	},

	postCard: {
		borderLeft: `4px solid #7b00b8`,
		transition: 'box-shadow 0.2s ease',
		'&:hover': { boxShadow: theme.shadows.md },
	},

	postTitle: {
		fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
		fontWeight: 700,
		fontSize: rem(28),
		lineHeight: 1.2,

		[theme.fn.smallerThan('sm')]: { fontSize: rem(22) },
	},

	previewCard: {
		cursor: 'pointer',
		transition: 'box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease',
		borderLeft: `3px solid transparent`,

		'&:hover': {
			boxShadow: theme.shadows.sm,
			transform: 'translateY(-2px)',
			borderLeftColor: '#7b00b8',
		},
	},

	previewTitle: {
		fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
		fontWeight: 600,
		fontSize: rem(14),
		lineHeight: 1.3,
	},

	dividerLabel: {
		fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
		fontWeight: 700,
		fontSize: rem(12),
		letterSpacing: '0.08em',
		textTransform: 'uppercase' as const,
		color: theme.colors.gray[5],
	},
}))

// ── Main component ────────────────────────────────────────────────────────────
function Blog() {
	const { classes } = useStyles()

	const links = [
		{ "link": "/About", "label": "About" },
		{ "link": "/Support", "label": "Support" },
		{ "link": "/Pp", "label": "Privacy" },
		{ "link": "/Tos", "label": "Terms of Use" },
		{ "link": "/Changelog", "label": "Changelog" },
	]

	const [searchParams, setSearchParams] = useSearchParams()
	const postIdParam = searchParams.get('postId')
	const activePost = postIdParam
		? blogPosts.find(p => p.id === parseInt(postIdParam))
		: blogPosts[0]

	const activePageIndex = blogPosts.findIndex(p => p.id === activePost?.id) + 1
	const currentUrl = window.location.href

	// ── New Content State Handling ───────────────────────────────────────────
	const [markdownContent, setMarkdownContent] = useState<string>('')
	const [isLoading, setIsLoading] = useState<boolean>(true)
	const [hasError, setHasError] = useState<boolean>(false)

	useEffect(() => {
		if (!activePost) return

		setIsLoading(true)
		setHasError(false)

		// Fetch the standalone markdown file dynamically from the public directory
		fetch(`/blog-posts/${activePost.id}.md`)
			.then((response) => {
				if (!response.ok) {
					throw new Error('Markdown file not found')
				}
				return response.text()
			})
			.then((data) => {
				setMarkdownContent(data)
				setIsLoading(false)
			})
			.catch((err) => {
				console.error('Error fetching blog post content:', err)
				setHasError(true)
				setIsLoading(false)
			})
	}, [activePost?.id]) // Fires whenever the page changes or a preview card is clicked

	// Back-to-top visibility
	const [showScrollTop, setShowScrollTop] = useState(false)
	const handleScroll = useCallback(() => {
		setShowScrollTop(window.scrollY > 400)
	}, [])
	useEffect(() => {
		window.addEventListener('scroll', handleScroll, { passive: true })
		return () => window.removeEventListener('scroll', handleScroll)
	}, [handleScroll])

	const handlePageChange = (newPage: number) => {
		const targetPost = blogPosts[newPage - 1]
		setSearchParams({ postId: targetPost.id.toString() })
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}

	const otherPosts = blogPosts.filter(p => p.id !== activePost?.id)

	return (
		<div className="blog-wrapper">

			{activePost && (
				<Helmet>
					<title>{activePost.title} | Comedy Seen Blog</title>
					<meta name="description" content={activePost.summary} />
					<meta property="og:type" content="article" />
					<meta property="og:title" content={activePost.title} />
					<meta property="og:description" content={activePost.summary} />
					<meta property="og:image" content={activePost.imageUrl} />
					<meta property="og:url" content={currentUrl} />
					<meta name="twitter:card" content="summary_large_image" />
					<meta name="twitter:title" content={activePost.title} />
					<meta name="twitter:description" content={activePost.summary} />
					<meta name="twitter:image" content={activePost.imageUrl} />
				</Helmet>
			)}

			<Head />

			{/* ── Hero ── */}
			<div className={classes.heroWrapper}>
				<Group position="center" spacing="xs" mb={6}>
					<IconPencil size={32} color="#7b00b8" />
				</Group>
				<Title className={classes.blogTitle}>PobbleBlog</Title>
				<Text size="md" color="dimmed" mt={6} italic>I know this is too wordy</Text>
			</div>

			<Container size="md" py="xl">

				{/* ── Active post ── */}
				{activePost && (
					<Card
						key={activePost.id}
						className={classes.postCard}
						shadow="sm"
						padding="xl"
						radius="md"
						withBorder
						mb="xl"
					>
						{/* Date badge */}
						<Group mb="sm">
							<Badge
								color="violet"
								variant="light"
								size="md"
								radius="sm"
								sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}
							>
								{activePost.date}
							</Badge>
						</Group>

						{/* Post title */}
						<Title order={2} className={classes.postTitle} mb="lg">
							{activePost.title}
						</Title>

						{/* Markdown content */}
						<div
							className="blog-prose"
							style={{
								fontSize: '1rem',
								lineHeight: '1.75',
								fontFamily: 'Georgia, serif',
								color: '#2d2d2d',
								minHeight: '150px' // Keeps layout stable while loader is running
							}}
						>
							{isLoading && (
								<Center py="xl">
									<Loader color="violet" size="md" variant="dots" />
								</Center>
							)}

							{hasError && (
								<Center py="xl">
									<Text color="red" size="sm" weight={500}>
										Bummer! Couldn't load the post text. Let's blame it on the tech and try refreshing.
									</Text>
								</Center>
							)}

							{!isLoading && !hasError && (
								<ReactMarkdown>
									{markdownContent}
								</ReactMarkdown>
							)}
						</div>


					</Card>
				)}

				{/* ── Centered pagination ── */}
				<Center mb="xl">
					<Pagination
						total={blogPosts.length}
						value={activePageIndex}
						onChange={handlePageChange}
						size="lg"
						color="violet"
					/>
				</Center>

				{/* ── Other posts preview strip ── */}
				{otherPosts.length > 0 && (
					<>
						<Divider
							mb="lg"
							label={<span className={classes.dividerLabel}>More posts</span>}
							labelPosition="center"
							size="xs"
						/>

						<Stack spacing="sm">
							{otherPosts.map(post => (
								<Card
									key={post.id}
									className={classes.previewCard}
									padding="md"
									radius="md"
									withBorder
									onClick={() => {
										setSearchParams({ postId: post.id.toString() })
										window.scrollTo({ top: 0, behavior: 'smooth' })
									}}
								>
									<Group position="apart" noWrap>
										<Stack spacing={2}>
											<Text className={classes.previewTitle}>{post.title}</Text>
											<Text size="xs" color="dimmed">{post.summary}</Text>
										</Stack>
										<Badge
											color="violet"
											variant="light"
											size="sm"
											radius="sm"
											sx={{ flexShrink: 0, fontFamily: "'Outfit', sans-serif" }}
										>
											{post.date}
										</Badge>
									</Group>
								</Card>
							))}
						</Stack>
					</>
				)}

				<Space h="xl" />
			</Container>

			<Footer links={links} />

			{/* ── Back to top ── */}
			<Affix position={{ bottom: rem(24), right: rem(24) }}>
				<Transition transition="slide-up" mounted={showScrollTop}>
					{(transitionStyles) => (
						<Button
							style={transitionStyles}
							color="violet"
							variant="filled"
							radius="xl"
							size="sm"
							leftIcon={<IconArrowUp size={16} />}
							onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
							sx={{
								boxShadow: '0 4px 16px rgba(91,0,153,0.3)',
								transition: 'transform 0.2s ease, box-shadow 0.2s ease',
								'&:hover': {
									transform: 'translateY(-2px)',
									boxShadow: '0 8px 24px rgba(91,0,153,0.4)',
								},
							}}
						>
							Top
						</Button>
					)}
				</Transition>
			</Affix>

		</div>
	)
}

export default Blog